import { useDolfinAuth } from "@/components/DolfinProvider";
import { useIsDemo } from "@/demo/DemoContext";
import type { components } from "@dolfin/api-admin";

export const DEMO_NOT_AVAILABLE = "Not available in a demo";
export const DEMO_NOTHING_SENT = "Nothing is sent from a demo";

export type OrganisationRole = components["schemas"]["OrganisationRole"];

export type Capability =
  | "approveBills"
  | "triggerPayment"
  | "markBillPaid"
  | "sendChase"
  | "manageSuppliers"
  | "manageApprovalPolicies"
  // Who may forward bills into the organisation's inbound address (the allowlist).
  | "manageBillForwarding"
  // Expense approval policies and confirming an uploaded expense policy document.
  | "manageExpensePolicies"
  | "resolvePolicyViolations"
  // Who holds a card, and who an expense is attributed to.
  | "assignSpenders"
  | "inviteUsers"
  | "changeOrgSettings"
  | "connectAccounting"
  | "connectBank"
  // Accounts receivable capabilities; Admin+ like the AP set.
  | "manageCustomers"
  | "manageProducts"
  | "manageTaxRates"
  | "manageInvoices"
  | "manageUsers";

// Mirror of the backend capability matrix; keep identical (eng-docs "AD - Organisation Role-Based Access Control" §3).
// The backend 403 is the real enforcement; this only drives the disable-with-tooltip UI.
const RULE: Record<Capability, OrganisationRole[]> = {
  approveBills: ["Owner", "Admin"],
  triggerPayment: ["Owner", "Admin"],
  markBillPaid: ["Owner", "Admin"],
  sendChase: ["Owner", "Admin"],
  manageSuppliers: ["Owner", "Admin"],
  manageApprovalPolicies: ["Owner", "Admin"],
  manageBillForwarding: ["Owner", "Admin"],
  manageExpensePolicies: ["Owner", "Admin"],
  // Accepting a policy violation overrides the organisation's own rule, so it is an admin decision.
  resolvePolicyViolations: ["Owner", "Admin"],
  assignSpenders: ["Owner", "Admin"],
  inviteUsers: ["Owner", "Admin"],
  changeOrgSettings: ["Owner", "Admin"],
  connectAccounting: ["Owner", "Admin"],
  // Admin or Owner, as POST/DELETE /bank-connections require.
  connectBank: ["Owner", "Admin"],
  manageCustomers: ["Owner", "Admin"],
  manageProducts: ["Owner", "Admin"],
  manageTaxRates: ["Owner", "Admin"],
  manageInvoices: ["Owner", "Admin"],
  manageUsers: ["Owner", "Admin"],
};

export function can(cap: Capability, role: OrganisationRole | null | undefined): boolean {
  return !!role && RULE[cap].includes(role);
}

/** Writes a demo organisation can't make, mirroring the backend's DemoWriteAllowlist; AR, AP and Expenses work is open. */
const DEMO_BLOCKED: Partial<Record<Capability, string>> = {
  // Outbound: answered Demo.OutboundBlocked.
  sendChase: DEMO_NOTHING_SENT,
  // Settings, people and real connections: answered Demo.ReadOnly.
  manageApprovalPolicies: DEMO_NOT_AVAILABLE,
  manageBillForwarding: DEMO_NOT_AVAILABLE,
  manageExpensePolicies: DEMO_NOT_AVAILABLE,
  inviteUsers: DEMO_NOT_AVAILABLE,
  changeOrgSettings: DEMO_NOT_AVAILABLE,
  connectAccounting: DEMO_NOT_AVAILABLE,
  connectBank: DEMO_NOT_AVAILABLE,
  manageTaxRates: DEMO_NOT_AVAILABLE,
  manageUsers: DEMO_NOT_AVAILABLE,
};

export function usePermissions() {
  const { role } = useDolfinAuth();
  const isDemo = useIsDemo();
  return {
    role,
    can: (cap: Capability) => !(isDemo && DEMO_BLOCKED[cap]) && can(cap, role),
    /** Why a capability is off in a demo, for the tooltip; null outside a demo or when the demo allows it. */
    demoReason: (cap: Capability) => (isDemo ? (DEMO_BLOCKED[cap] ?? null) : null),
    /** The role's rule alone, for reads that mirror a write's role (seeing balances): a demo's admin still sees them. */
    roleAllows: (cap: Capability) => can(cap, role),
  };
}
